import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2, Mail, Bell, MessageSquare, Users, MapPin, Send, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

/**
 * InviteTalentModal
 * Mass-invite talent to a shift with filters + multi-channel (email/in-app/sms).
 */
const InviteTalentModal = ({ open, onOpenChange, shiftId, shiftMeta, apiBase = '/admin', initialAsBackup = false, autoNearbyRadius = null }) => {
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sending, setSending] = useState(false);

  // Filters
  // Iter-314 — Market-safety defaults. `onlyMatchingLocation` now
  // defaults to TRUE so admins never accidentally blast an invite
  // nationally without an explicit opt-in. The old default (false +
  // radius=75) pulled talent from adjacent metros silently. Admins who
  // want a national send must now toggle "Invite All Talent" explicitly.
  const [inviteAll, setInviteAll] = useState(false);
  // Iter-315b — Explicit nationwide opt-in. Server now REJECTS invite_all
  // without this flag. Prevents accidental blasts to every talent.
  const [confirmNationwide, setConfirmNationwide] = useState(false);
  // 2026-09 — Invite talent specifically as a BACKUP (vs primary). Works with
  // every audience mode (individual, radius, market, Invite ALL).
  const [asBackup, setAsBackup] = useState(initialAsBackup);
  // Sync backup mode whenever the modal is (re)opened from a backup-needed card.
  useEffect(() => { if (open) setAsBackup(initialAsBackup); }, [open, initialAsBackup]);

  // 2026-09 (Phase 1) — Venue-distance "Find Nearby Talent" for last-minute
  // staffing/backups. Uses the shift's venue → talent's SAVED home coords.
  const [nearbyRadius, setNearbyRadius] = useState(null); // active preset in miles
  const [nearbyLoading, setNearbyLoading] = useState(false);
  const [eligibleOnly, setEligibleOnly] = useState(true); // only show instantly-bookable talent
  const findNearby = async (radius, eligibleOnlyOverride) => {
    if (!shiftId) return;
    const elig = eligibleOnlyOverride == null ? eligibleOnly : eligibleOnlyOverride;
    setNearbyRadius(radius);
    setNearbyLoading(true);
    setTalentQuery('');
    try {
      const res = await api.get(`${apiBase}/talent/lookup`, {
        params: { nearby: true, shift_id: shiftId, radius_miles: radius, eligible_only: elig, limit: 50 },
      });
      setTalentMatches(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setTalentMatches([]);
      const code = err?.response?.status;
      toast.error(code === 403
        ? 'You do not have access to this gig.'
        : 'Could not load nearby talent. Please try again.');
    } finally {
      setNearbyLoading(false);
    }
  };
  // "Find Replacement Nearby" — when opened with an autoNearbyRadius, run the
  // bookable-nearby search immediately so the admin lands on a ready-to-invite
  // list of the closest available/qualified/unbooked talent.
  useEffect(() => {
    if (open && autoNearbyRadius) {
      findNearby(autoNearbyRadius, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, autoNearbyRadius]);
  const [onlyMatchingLocation, setOnlyMatchingLocation] = useState(true);
  const [radiusMiles, setRadiusMiles] = useState(75); // used when onlyMatchingLocation is toggled off
  const [tier, setTier] = useState('any');
  // Iter-297 — Market multi-select replaces City Override / State
  // Override text inputs (admins kept typing names that didn't match
  // the slug, getting "No talent matches" silently).
  const [selectedMarketIds, setSelectedMarketIds] = useState([]);
  // Iter-297 — Direct talent lookup by name / email / phone / id.
  const [talentQuery, setTalentQuery] = useState('');
  const [talentMatches, setTalentMatches] = useState([]);
  const [talentSearching, setTalentSearching] = useState(false);
  const [selectedTalent, setSelectedTalent] = useState([]); // [{user_id, display_name, email, ...}]

  // Channels
  const [sendEmail, setSendEmail] = useState(true);
  const [sendInApp, setSendInApp] = useState(true);
  const [sendSms, setSendSms] = useState(false);

  // Message
  const [customMessage, setCustomMessage] = useState('');

  const usingSpecificTalent = selectedTalent.length > 0;
  // When the admin is picking people (nearby radius search active OR specific
  // talent chosen), we hide the market/city AUDIENCE preview + "Invite ALL"
  // block — otherwise a "0 nearby" list contradicts a market "N will receive"
  // count. Sending then targets exactly the selected people.
  const pickingSpecific = usingSpecificTalent || nearbyRadius != null;
  const usingMarkets = selectedMarketIds.length > 0;

  const buildParams = () => {
    // Specific talent mode: pin the audience to the picked user IDs.
    // Iter-369 (Feb 2026) — previously we skipped the preview entirely
    // and displayed selectedTalent.length; the send then applied hygiene
    // filters and dropped to 0, contradicting the modal. Now we hit
    // the SAME endpoint the send does so the count is honest.
    if (usingSpecificTalent) {
      return {
        active_only: true,
        invite_all: false,
        target_user_ids: selectedTalent.map((t) => t.user_id).join(','),
      };
    }
    const params = {
      active_only: true,
      invite_all: inviteAll,
    };
    if (!inviteAll) {
      // Iter-360f (P0-356) — markets + radius are now COMBINABLE. Passing
      // both lets an operator scope to "Atlanta market talent within 50 mi
      // of the venue". Backend Iter-360b already interprets `radius=null`
      // as "no distance limit" — pairing it with a market means
      // "everyone the market covers" (e.g., all of Georgia for Atlanta).
      if (usingMarkets) {
        params.market_ids = selectedMarketIds.join(',');
      }
      params.only_matching_location = onlyMatchingLocation;
      if (!onlyMatchingLocation && radiusMiles) {
        params.radius_miles = radiusMiles;
      }
      if (tier && tier !== 'any') params.tier_required = tier;
    }
    return params;
  };

  const fetchPreview = async () => {
    if (!shiftId) return;
    setLoadingPreview(true);
    try {
      const res = await api.get(`${apiBase}/shifts/${shiftId}/invite-preview`, { params: buildParams() });
      setPreview(res.data);
    } catch (e) {
      toast.error('Preview failed');
    } finally {
      setLoadingPreview(false);
    }
  };

  useEffect(() => {
    if (open) fetchPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, inviteAll, onlyMatchingLocation, radiusMiles, selectedMarketIds, selectedTalent, tier]);

  // Iter-297 — debounced talent search.
  useEffect(() => {
    const q = talentQuery.trim();
    if (q.length < 2) {
      setTalentMatches([]);
      return undefined;
    }
    const handle = setTimeout(async () => {
      setTalentSearching(true);
      try {
        const res = await api.get(`${apiBase}/talent/lookup`, { params: { q, limit: 8, shift_id: shiftId } });
        setTalentMatches(Array.isArray(res.data) ? res.data : []);
      } catch {
        setTalentMatches([]);
      } finally {
        setTalentSearching(false);
      }
    }, 280);
    return () => clearTimeout(handle);
  }, [talentQuery]);

  const toggleMarket = (marketId) => {
    if (!marketId) return;
    setSelectedMarketIds((prev) =>
      prev.includes(marketId) ? prev.filter((m) => m !== marketId) : [...prev, marketId]
    );
  };

  const addTalent = (t) => {
    setSelectedTalent((prev) =>
      prev.some((p) => p.user_id === t.user_id) ? prev : [...prev, t]
    );
    setTalentQuery('');
    setTalentMatches([]);
  };

  const removeTalent = (userId) => {
    setSelectedTalent((prev) => prev.filter((p) => p.user_id !== userId));
  };

  // Iter-435 — select every ELIGIBLE shown candidate in ONE merge. The
  // old code looped addTalent() (which also cleared talentMatches on each
  // call), so only a single talent ended up selected. This dedupes against
  // the current selection and leaves the results list visible.
  const selectAllShown = () => {
    const eligible = talentMatches.filter((t) => t.eligible !== false);
    if (eligible.length === 0) return;
    setSelectedTalent((prev) => {
      const seen = new Set(prev.map((p) => p.user_id));
      const additions = eligible.filter((t) => !seen.has(t.user_id));
      return additions.length ? [...prev, ...additions] : prev;
    });
  };

  const handleSend = async () => {
    if (!sendEmail && !sendInApp && !sendSms) {
      toast.error('Select at least one channel');
      return;
    }
    // Iter-315b — Nationwide guardrail (mirrors backend).
    if (inviteAll && !confirmNationwide) {
      toast.error('You must confirm the Nationwide broadcast before sending.');
      return;
    }
    if (!preview || preview.total_matching === 0) {
      toast.error('No talent matches the current filters');
      return;
    }
    if (preview.total_matching > 500) {
      const ok = window.confirm(
        `This will send invites to ${preview.total_matching} people. Continue?`
      );
      if (!ok) return;
    }
    setSending(true);
    try {
      const body = {
        active_only: true,
        invite_all: inviteAll && !usingSpecificTalent,
        // Iter-315b — Server rejects invite_all without this flag.
        confirm_nationwide: inviteAll && confirmNationwide,
        // Iter-297 — Specific talent mode wins outright.
        target_user_ids: usingSpecificTalent ? selectedTalent.map((t) => t.user_id) : null,
        // Iter-360f (P0-356) — markets + radius are combinable now.
        market_ids: !usingSpecificTalent && !inviteAll && usingMarkets ? selectedMarketIds : null,
        only_matching_location:
          !usingSpecificTalent && !inviteAll && onlyMatchingLocation,
        radius_miles:
          !usingSpecificTalent && !inviteAll && !onlyMatchingLocation
            ? radiusMiles
            : null,
        cities: null,
        states: null,
        tier_required:
          !usingSpecificTalent && !inviteAll && tier !== 'any' ? tier : null,
        send_email: sendEmail,
        send_in_app: sendInApp,
        send_sms: sendSms,
        custom_message: customMessage.trim() || null,
        as_backup: asBackup,
      };
      const res = await api.post(`${apiBase}/shifts/${shiftId}/invite-talent`, body);
      const r = res.data || {};

      // Iter-185 P0 — handle the new async/queued response shape for
      // large batches (>75 recipients). Show a real status message.
      if (r.queued) {
        toast.success(
          r.message || `Invites queued for ${r.queued_count?.toLocaleString?.() ?? r.recipients_matched} talent — sending in the background.`,
          { duration: 7000 }
        );
        onOpenChange(false);
        return;
      }

      const s = r.sent || { email: 0, in_app: 0, sms: 0 };
      const f = r.failed || { email: 0, in_app: 0, sms: 0 };
      const totalSent = (s.email || 0) + (s.in_app || 0) + (s.sms || 0);
      const totalFailed = (f.email || 0) + (f.in_app || 0) + (f.sms || 0);

      if (totalSent === 0 && totalFailed > 0) {
        // Nothing actually went through — surface the first failure reason.
        const first = (r.failure_samples || [])[0];
        const reason = first?.reason || 'send failed';
        toast.error(`Failed to send invites — ${reason}`, { duration: 8000 });
      } else if (totalFailed > 0) {
        const first = (r.failure_samples || [])[0];
        const reasonHint = first?.reason ? ` (first error: ${first.reason})` : '';
        toast.warning(
          `Invites partially sent — ${s.email} email · ${s.in_app} in-app · ${s.sms} SMS · ${totalFailed} failed${reasonHint}`,
          { duration: 8000 }
        );
      } else {
        toast.success(
          `Invites sent — ${s.email} email · ${s.in_app} in-app · ${s.sms} SMS`,
          { duration: 5000 }
        );
      }
      onOpenChange(false);
    } catch (e) {
      // Iter-358 (P0-356) — Structured detail dict with {code, message,
      // audience_hints} triggers a longer, actionable toast. Plain
      // string details fall back to the legacy short toast.
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.message) {
        toast.error(detail.message, { duration: 10000 });
      } else {
        toast.error(detail || 'Failed to send invites');
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="invite-talent-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="w-5 h-5 text-cyan-500" />
            Invite Talent to This Job
          </DialogTitle>
          <DialogDescription className="text-xs text-gray-500">
            Search for talent and send them a direct invite to apply to this job.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* 2026-09 — Invite as Primary or Backup. Applies to every audience mode. */}
          <div className="space-y-2" data-testid="invite-role-selector">
            <Label className="text-sm font-semibold">Invite them as</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setAsBackup(false)}
                className={`p-3 border rounded-lg text-left transition-colors ${!asBackup ? 'border-cyan-500 bg-cyan-50 ring-1 ring-cyan-200' : 'border-gray-200 hover:bg-gray-50'}`}
                data-testid="invite-as-primary-btn"
              >
                <div className="text-sm font-bold text-gray-900">Primary</div>
                <div className="text-[11px] text-gray-500">The main booked talent for this role.</div>
              </button>
              <button
                type="button"
                onClick={() => setAsBackup(true)}
                className={`p-3 border rounded-lg text-left transition-colors ${asBackup ? 'border-amber-500 bg-amber-50 ring-1 ring-amber-200' : 'border-gray-200 hover:bg-gray-50'}`}
                data-testid="invite-as-backup-btn"
              >
                <div className="text-sm font-bold text-gray-900 flex items-center gap-1.5">
                  Backup
                  {asBackup && <span className="text-[9px] uppercase tracking-wide font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">backup</span>}
                </div>
                <div className="text-[11px] text-gray-500">Accepts as a backup — stays a backup until you activate them.</div>
              </button>
            </div>
            {asBackup && (
              <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5" data-testid="invite-backup-hint">
                Backups can be invited from anywhere — pick specific people or use the market/radius tools below. They must accept the invite; accepting never makes them a primary.
              </p>
            )}
          </div>

          {/* Filters */}
          <div className="space-y-3">
            <Label className="text-sm font-semibold">Who should be invited?</Label>
            <p className="text-[11px] text-gray-500 -mt-1">Start with the closest suitable people below — search by name or tap a radius under “Find nearby talent”.</p>
            <label className={`flex items-center gap-2 p-2 border rounded-lg cursor-pointer transition ${inviteAll ? 'border-amber-400 bg-amber-50' : 'border-gray-200 opacity-80 hover:opacity-100'}`}>
              <Checkbox
                checked={inviteAll}
                onCheckedChange={(v) => { setInviteAll(!!v); if (!v) setConfirmNationwide(false); }}
                data-testid="invite-all-checkbox"
              />
              <div>
                <div className="text-xs font-semibold text-gray-600 flex items-center gap-1">
                  <Users className="w-3.5 h-3.5" /> Blast ALL active talent (nationwide) {inviteAll && <span className="ml-1 text-[10px] uppercase tracking-wide font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">nationwide</span>}
                </div>
                <div className="text-[10px] text-gray-400">
                  {inviteAll
                    ? '⚠️ Notifies every eligible active talent in EVERY market. Usually not what you want for a local or last-minute shift.'
                    : 'Last resort — reaches everyone everywhere. Prefer nearby talent above.'}
                </div>
              </div>
            </label>

            {/* Iter-315b — Explicit nationwide confirmation gate. */}
            {inviteAll && (
              <label className="ml-6 flex items-start gap-2 p-3 border-2 rounded-lg border-red-300 bg-red-50" data-testid="confirm-nationwide-gate">
                <Checkbox
                  checked={confirmNationwide}
                  onCheckedChange={(v) => setConfirmNationwide(!!v)}
                  data-testid="confirm-nationwide-checkbox"
                />
                <div className="text-xs text-red-900">
                  <div className="font-bold uppercase tracking-wide mb-1">Confirm Nationwide Broadcast</div>
                  <div>
                    I understand this will invite talent in EVERY market, not just{' '}
                    <strong>{shiftMeta?.city || 'this event\'s city'}, {shiftMeta?.state || ''}</strong>.
                    The system will reject the send until this is checked.
                  </div>
                </div>
              </label>
            )}

            {!inviteAll && (
              <>
                {/* Iter-297 — Specific talent lookup. Selecting talent
                    via this search short-circuits every other filter. */}
                <div className="p-3 border rounded-lg bg-violet-50/40 border-violet-200 space-y-2" data-testid="talent-lookup-card">
                  <Label className="text-sm font-semibold text-violet-900 flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    Or invite specific talent by name / email / ID
                  </Label>
                  <Input
                    value={talentQuery}
                    onChange={(e) => setTalentQuery(e.target.value)}
                    placeholder="Search talent — name, email, phone, or ID"
                    data-testid="invite-talent-lookup-input"
                  />
                  {/* Find Nearby Talent — venue-distance radius presets */}
                  {shiftId && (
                    <div className="pt-1" data-testid="find-nearby-talent">
                      <div className="text-[11px] font-semibold text-gray-500 mb-1 flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> Find nearby talent (from venue)
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {[5, 10, 15, 25, 50].map((mi) => (
                          <button
                            key={mi}
                            type="button"
                            onClick={() => findNearby(mi)}
                            className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition ${nearbyRadius === mi ? 'bg-cyan-600 text-white border-cyan-600' : 'bg-white text-cyan-700 border-cyan-200 hover:border-cyan-400'}`}
                            data-testid={`nearby-radius-${mi}`}
                          >
                            {mi} mi
                          </button>
                        ))}
                        <button
                          type="button"
                          onClick={() => { const v = window.prompt('Custom radius (miles):', '20'); const n = parseFloat(v); if (n > 0) findNearby(n); }}
                          className="text-[11px] font-semibold px-2.5 py-1 rounded-full border bg-white text-cyan-700 border-cyan-200 hover:border-cyan-400"
                          data-testid="nearby-radius-custom"
                        >
                          Custom
                        </button>
                      </div>
                      <label className="flex items-center gap-1.5 mt-2 cursor-pointer select-none" data-testid="eligible-only-toggle">
                        <input
                          type="checkbox"
                          checked={eligibleOnly}
                          onChange={(e) => { const v = e.target.checked; setEligibleOnly(v); if (nearbyRadius != null) findNearby(nearbyRadius, v); }}
                          className="h-3.5 w-3.5 accent-cyan-600"
                        />
                        <span className="text-[11px] text-gray-600">
                          Only bookable talent <span className="text-gray-400">(available, right tier, not already booked)</span>
                        </span>
                      </label>
                    </div>
                  )}
                  {(talentSearching || nearbyLoading) && (
                    <p className="text-[11px] text-violet-700">{nearbyLoading ? `Finding talent within ${nearbyRadius} mi…` : 'Searching…'}</p>
                  )}
                  {!talentSearching && !nearbyLoading && nearbyRadius != null && talentMatches.length === 0 && (
                    <p className="text-[11px] text-gray-500" data-testid="nearby-empty">
                      {eligibleOnly
                        ? `No bookable talent within ${nearbyRadius} mi. Uncheck "Only bookable talent" to see everyone nearby.`
                        : `No talent found within ${nearbyRadius} mi of the venue.`}
                    </p>
                  )}
                  {!talentSearching && !nearbyLoading && talentMatches.length > 0 && (
                    <div className="rounded border border-violet-200 bg-white" data-testid="talent-results-list">
                      <div className="flex items-center justify-between px-3 py-1.5 border-b border-violet-100 bg-violet-50/50">
                        <span className="text-[11px] font-semibold text-violet-800">
                          {talentMatches.length} {nearbyRadius != null ? `within ${nearbyRadius} mi` : 'match' + (talentMatches.length === 1 ? '' : 'es')} · nearest first
                        </span>
                        <button
                          type="button"
                          className="text-[11px] font-semibold text-violet-700 hover:underline disabled:opacity-40 disabled:no-underline"
                          data-testid="select-all-shown-btn"
                          disabled={talentMatches.filter(t => t.eligible !== false).length === 0}
                          onClick={selectAllShown}
                        >
                          Select all shown ({talentMatches.filter(t => t.eligible !== false).length})
                        </button>
                      </div>
                      <div className="max-h-72 overflow-y-auto divide-y divide-violet-100">
                        {talentMatches.map((t) => {
                          const selected = selectedTalent.some(s => s.user_id === t.user_id);
                          return (
                            <div
                              key={t.user_id}
                              className={`flex items-start gap-2.5 px-3 py-2 transition ${selected ? 'bg-cyan-50' : 'hover:bg-violet-50'}`}
                              data-testid={`talent-match-${t.user_id}`}
                            >
                              <input
                                type="checkbox"
                                checked={selected}
                                disabled={t.eligible === false}
                                onChange={() => selected ? removeTalent(t.user_id) : addTalent(t)}
                                className="mt-1.5 h-4 w-4 accent-cyan-600 shrink-0 disabled:opacity-40"
                                data-testid={`talent-checkbox-${t.user_id}`}
                              />
                              {t.photo ? (
                                <img src={t.photo} alt="" className="w-9 h-9 rounded-full object-cover bg-gray-100 shrink-0" />
                              ) : (
                                <div className="w-9 h-9 rounded-full bg-violet-100 text-violet-700 grid place-items-center text-xs font-bold shrink-0">
                                  {(t.first_name || t.display_name || '?').slice(0, 1).toUpperCase()}
                                </div>
                              )}
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center justify-between gap-2">
                                  <span className="text-sm font-semibold text-gray-900 truncate">{t.display_name}</span>
                                  {shiftId && (t.distance_available ? (
                                    <span className="text-[11px] font-bold text-cyan-700 shrink-0" data-testid={`talent-distance-${t.user_id}`}>
                                      {t.distance_approx ? '~' : ''}{t.distance_miles} mi
                                    </span>
                                  ) : (
                                    <span className="text-[10px] text-gray-400 shrink-0" data-testid={`talent-distance-${t.user_id}`}>Distance N/A</span>
                                  ))}
                                </div>
                                <div className="text-[11px] text-gray-500 truncate">
                                  {t.role_name ? `${t.role_name} · ` : ''}{t.city ? `${t.city}, ${t.state}` : (t.email || '')}
                                </div>
                                <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                                  {t.tier && <span className="text-[9px] uppercase tracking-wide bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded-full">{t.tier}</span>}
                                  {t.average_rating != null && t.total_ratings > 0 && (
                                    <span className="text-[10px] text-amber-600 font-semibold">★ {t.average_rating.toFixed(1)}</span>
                                  )}
                                  {t.already_invited && <span className="text-[9px] font-bold text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded-full" data-testid={`talent-invited-${t.user_id}`}>Already invited</span>}
                                  {t.already_booked && <span className="text-[9px] font-bold text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded-full" data-testid={`talent-booked-${t.user_id}`}>On this shift</span>}
                                  {t.has_conflict && <span className="text-[9px] font-bold text-rose-700 bg-rose-100 px-1.5 py-0.5 rounded-full" data-testid={`talent-conflict-${t.user_id}`}>Booked that day</span>}
                                  {(t.ineligibility_reasons || []).includes('tier_mismatch') && <span className="text-[9px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full" data-testid={`talent-tier-mismatch-${t.user_id}`}>Tier mismatch</span>}
                                  {(t.market_mismatch || (t.ineligibility_reasons || []).includes('market_mismatch')) && <span className="text-[9px] font-bold text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded-full" data-testid={`talent-market-mismatch-${t.user_id}`}>Outside work market</span>}
                                  {t.eligible === true && <span className="text-[9px] font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-full" data-testid={`talent-bookable-${t.user_id}`}>Bookable</span>}
                                  <a
                                    href={`/community/member/${t.user_id}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="text-[10px] font-semibold text-cyan-700 hover:underline"
                                    data-testid={`talent-view-${t.user_id}`}
                                  >
                                    View profile
                                  </a>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {selectedTalent.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 pt-1" data-testid="selected-talent-chips">
                      {selectedTalent.map((t) => (
                        <Badge
                          key={t.user_id}
                          className="bg-violet-500 text-white hover:bg-violet-600 cursor-pointer"
                          onClick={() => removeTalent(t.user_id)}
                          title="Click to remove"
                          data-testid={`selected-talent-chip-${t.user_id}`}
                        >
                          {t.display_name}
                          <span className="ml-1 text-xs">×</span>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {!pickingSpecific && (
                  <>
                    {/* Iter-360f (P0-356 follow-up) — Explicit audience-
                        scope radio group so the four operator intents
                        (statewide / radius / city-only / nationwide)
                        are visible and one-tap. */}
                    <label className="flex items-start gap-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50"
                           data-testid="audience-scope-city">
                      <Checkbox
                        checked={onlyMatchingLocation}
                        onCheckedChange={(v) => setOnlyMatchingLocation(!!v)}
                        data-testid="only-matching-location-checkbox"
                      />
                      <div className="text-sm font-medium text-gray-900 flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        Shift city only ({shiftMeta?.city || '—'}, {shiftMeta?.state || '—'})
                      </div>
                    </label>

                    {!onlyMatchingLocation && (
                      <>
                        <div className="pl-2" data-testid="invite-radius-selector">
                          <Label className="text-xs text-gray-500 mb-1 block">
                            Radius around {shiftMeta?.city || 'shift'} (metro reach)
                          </Label>
                          <div className="flex flex-wrap gap-1.5">
                            {[25, 50, 75, 100, 150].map((r) => (
                              <button
                                key={r}
                                type="button"
                                onClick={() => setRadiusMiles(r)}
                                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                                  radiusMiles === r
                                    ? 'bg-cyan-500 text-white border-cyan-400 shadow'
                                    : 'bg-white text-gray-700 border-gray-200 hover:border-cyan-300'
                                }`}
                                data-testid={`invite-radius-${r}`}
                              >
                                {r} mi
                              </button>
                            ))}
                            {/* Iter-360f — Clear the radius so the state
                                / market scope takes precedence. */}
                            <button
                              type="button"
                              onClick={() => setRadiusMiles(null)}
                              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                                radiusMiles == null
                                  ? 'bg-slate-800 text-white border-slate-700'
                                  : 'bg-white text-gray-700 border-gray-200 hover:border-slate-400'
                              }`}
                              data-testid="invite-radius-none"
                            >
                              No radius (statewide / markets)
                            </button>
                          </div>
                          <div className="text-[11px] text-gray-400 mt-1">
                            Defaults to 75 mi. Pick <b>No radius</b> to send to the entire
                            market (e.g. Atlanta → all of Georgia).
                          </div>
                        </div>

                        {/* Iter-297 — Multi-market chip selector. Replaces the old
                            free-form Cities / States override inputs. The chips below
                            preview come pre-filled from the recipient breakdown so an
                            admin can click "Atlanta · 412" and instantly add that
                            metro to the audience. */}
                        <div className="space-y-1 pl-2" data-testid="invite-markets-selector">
                          <Label className="text-xs text-gray-500">
                            Specific markets
                          </Label>
                          <p className="text-[11px] text-gray-400">
                            Click any market chip in the preview below to add it. Empty =
                            use the radius reach above.
                          </p>
                          {selectedMarketIds.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 pt-1">
                              {selectedMarketIds.map((mid) => {
                                const meta = (preview?.market_breakdown || []).find(
                                  (m) => m.id === mid
                                );
                                return (
                                  <Badge
                                    key={mid}
                                    className="bg-emerald-500 text-white hover:bg-emerald-600 cursor-pointer"
                                    onClick={() => toggleMarket(mid)}
                                    title="Click to remove"
                                    data-testid={`selected-market-chip-${mid}`}
                                  >
                                    {(meta?.metro_name || 'Market').split(',')[0]}
                                    <span className="ml-1 text-xs">×</span>
                                  </Badge>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </>
                    )}

                    <div className="space-y-1 pl-2">
                      <Label className="text-xs text-gray-500">Tier (optional)</Label>
                      <Select value={tier} onValueChange={setTier}>
                        <SelectTrigger className="w-44" data-testid="invite-tier-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="any">Any tier</SelectItem>
                          <SelectItem value="flex">Flex</SelectItem>
                          <SelectItem value="verified">Verified</SelectItem>
                          <SelectItem value="premium">Premium</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}
              </>
            )}

            {/* Preview */}
            <div className="p-3 bg-cyan-50 border border-cyan-200 rounded-lg flex flex-col gap-2" data-testid="invite-preview-box">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm">
                  {usingSpecificTalent ? (
                    <>
                      <Users className="w-4 h-4 text-cyan-600" />
                      <span className="font-semibold text-cyan-900" data-testid="invite-preview-count">{selectedTalent.length}</span>
                      <span className="text-cyan-700">selected — will receive this invite</span>
                    </>
                  ) : pickingSpecific ? (
                    <span className="text-cyan-700" data-testid="invite-preview-pick-hint">Select people from the list above, then Invite Selected Talent.</span>
                  ) : loadingPreview ? (
                    <><Loader2 className="w-4 h-4 animate-spin text-cyan-500" /> Calculating…</>
                  ) : (
                    <>
                      <Users className="w-4 h-4 text-cyan-600" />
                      <span className="font-semibold text-cyan-900" data-testid="invite-preview-count">
                        {preview?.total_matching ?? 0}
                      </span>
                      <span className="text-cyan-700">talent will actually receive this invite</span>
                    </>
                  )}
                </div>
                {!usingSpecificTalent && preview?.total_matching > 500 && (
                  <Badge className="bg-amber-100 text-amber-800 flex items-center gap-1 text-xs">
                    <AlertCircle className="w-3 h-3" /> Large batch
                  </Badge>
                )}
              </div>

              {/* Iter-369 — Per-target eligibility panel for specific-mode. */}
              {!loadingPreview && Array.isArray(preview?.blocked_targets) && preview.blocked_targets.length > 0 && (
                <div className="rounded-lg border-2 border-rose-300 bg-rose-50 p-3 text-sm space-y-2" data-testid="invite-preview-blocked-targets">
                  <p className="font-bold text-rose-900 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    {preview.blocked_targets.length} of your selected talent cannot be invited
                  </p>
                  <ul className="space-y-1 text-rose-800 list-disc list-inside">
                    {preview.blocked_targets.map((b) => (
                      <li key={b.user_id} data-testid={`invite-blocked-${b.user_id}`}>
                        <strong>{b.name}</strong> — {b.reasons.join(', ')}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Iter-366 — Full audience breakdown so operators see the
                  exact drop-off between "profiles in the market" and
                  "profiles the invite pipeline will actually reach". */}
              {!pickingSpecific && !loadingPreview && preview?.breakdown && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2 border-t border-cyan-200/60" data-testid="invite-preview-breakdown">
                  <div className="rounded bg-white border border-cyan-200 p-2" data-testid="breakdown-registered">
                    <p className="text-[10px] uppercase tracking-wide text-cyan-700">Registered in audience</p>
                    <p className="text-lg font-bold text-cyan-900">{preview.breakdown.registered_in_audience ?? 0}</p>
                  </div>
                  <div className="rounded bg-white border border-amber-200 p-2" data-testid="breakdown-excluded">
                    <p className="text-[10px] uppercase tracking-wide text-amber-700">Excluded (hygiene)</p>
                    <p className="text-lg font-bold text-amber-800">{preview.breakdown.excluded_by_hygiene ?? 0}</p>
                    <p className="text-[10px] text-amber-700/80">IDV / test / inactive</p>
                  </div>
                  <div className="rounded bg-white border border-slate-200 p-2" data-testid="breakdown-already-invited">
                    <p className="text-[10px] uppercase tracking-wide text-slate-600">Already invited</p>
                    <p className="text-lg font-bold text-slate-800">{preview.breakdown.already_invited ?? 0}</p>
                  </div>
                  <div className="rounded bg-emerald-50 border-2 border-emerald-300 p-2" data-testid="breakdown-final">
                    <p className="text-[10px] uppercase tracking-wide text-emerald-700 font-bold">Will receive invite</p>
                    <p className="text-lg font-bold text-emerald-900">{preview.breakdown.final_count ?? preview.total_matching ?? 0}</p>
                  </div>
                </div>
              )}

              {/* Iter-191.1 / Iter-366 — Recipients grouped by market they
                  belong to (link ∪ state coverage ∪ city coverage). Each
                  chip's number = recipients IN this filtered audience who
                  also belong to that market. Clicking scopes the invite
                  to that market. */}
              {!pickingSpecific && !loadingPreview && Array.isArray(preview?.market_breakdown) && preview.market_breakdown.length > 0 && (
                <div className="flex flex-wrap gap-1 pt-1 border-t border-cyan-200/60" data-testid="invite-preview-markets">
                  <span
                    className="text-[11px] uppercase tracking-wide text-cyan-700/80 mr-1 self-center"
                    title="Each chip shows how many of the recipients above ALSO belong to that market (via explicit link, state coverage, or city coverage). Totals across chips may overlap because talent can belong to multiple markets."
                  >
                    Recipients by market:
                  </span>
                  {preview.market_breakdown.slice(0, 8).map((b, i) => {
                    const isShiftMarket = shiftMeta?.market_id && b.id === shiftMeta.market_id;
                    const isSelected = b.id && selectedMarketIds.includes(b.id);
                    const isClickable = !!b.id && !usingSpecificTalent && !inviteAll;
                    return (
                      <button
                        key={b.id || `no-mkt-${i}`}
                        type="button"
                        disabled={!isClickable}
                        onClick={() => b.id && toggleMarket(b.id)}
                        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] border font-medium transition ${
                          isSelected
                            ? 'bg-violet-600 text-white border-violet-700 ring-2 ring-violet-200'
                            : isShiftMarket
                              ? 'bg-emerald-500 text-white border-emerald-500 ring-2 ring-emerald-200'
                              : b.id
                                ? 'bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100 hover:border-emerald-400'
                                : 'bg-gray-100 text-gray-600 border-gray-200'
                        } ${isClickable ? 'cursor-pointer' : 'cursor-default opacity-80'}`}
                        data-testid={`invite-preview-market-chip-${b.slug || 'unmarked'}`}
                        title={`${b.recipients} of the ${preview?.total_matching ?? 0} recipients above also belong to ${b.metro_name}. ${isSelected
                            ? 'Click to remove from invite audience'
                            : isClickable
                              ? 'Click to scope the invite to this market'
                              : ''}`}
                      >
                        {(b.metro_name || 'No market').split(',')[0].split('/')[0].trim()}
                        <strong className="font-bold">{b.recipients}</strong>
                        {isSelected && <span className="ml-0.5">✓</span>}
                        {!isSelected && isShiftMarket && <span className="ml-0.5">📍</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Channels */}
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Channels</Label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <Checkbox checked={sendEmail} onCheckedChange={(v) => setSendEmail(!!v)} data-testid="channel-email" />
                <Mail className="w-4 h-4 text-blue-500" />
                <span className="text-sm font-medium">Email (recommended)</span>
              </label>
              <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <Checkbox checked={sendInApp} onCheckedChange={(v) => setSendInApp(!!v)} data-testid="channel-in-app" />
                <Bell className="w-4 h-4 text-cyan-500" />
                <span className="text-sm font-medium">In-app</span>
              </label>
              <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <Checkbox checked={sendSms} onCheckedChange={(v) => setSendSms(!!v)} data-testid="channel-sms" />
                <MessageSquare className="w-4 h-4 text-green-500" />
                <span className="text-sm font-medium">SMS</span>
              </label>
            </div>
          </div>

          {/* Custom message */}
          <div className="space-y-1">
            <Label className="text-sm font-semibold">Custom message (optional)</Label>
            <Textarea
              rows={2}
              maxLength={280}
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              placeholder="Leave blank for the default 'New gig available...' message."
              data-testid="invite-custom-message"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>Cancel</Button>
          <Button
            onClick={handleSend}
            disabled={
              sending || (
                usingSpecificTalent
                  ? selectedTalent.length === 0
                  : (!preview || preview.total_matching === 0 || (inviteAll && !confirmNationwide))
              )
            }
            className="bg-cyan-500 hover:bg-cyan-600 text-white"
            data-testid="send-invites-btn"
          >
            {sending
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending…</>
              : usingSpecificTalent
                ? <><Send className="w-4 h-4 mr-2" />Invite Selected Talent ({selectedTalent.length})</>
                : inviteAll
                  ? <><Send className="w-4 h-4 mr-2" />Invite All Matching Talent</>
                  : <><Send className="w-4 h-4 mr-2" />Send Invites</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InviteTalentModal;
